import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DoCheck,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { OverlayManagerComponent } from '@client-monorepo/common/ui-components';
import { InAppMessageApiResponseType, InAppMessageDialogComponent, InAppMessagesApiService } from '@client-monorepo/common/in-app-messages';
import { NgxBottomNavigationService, NgxBottomNavigationWrapperComponent } from '@digipay/ngx-bottom-navigation';
import {
  AbTestService,
  DeviceDetector,
  EmitterService,
  EmittingDataEnum,
  LayoutService,
  StorageService,
  SupportMessengerService,
} from '@client-monorepo/common/utilities';
import { filter, fromEvent, Subscription } from 'rxjs';
import { AuthService, ProfileInterface, UserApiService } from '@client-monorepo/common/user';
import { ErrorPageComponent } from '@client-monorepo/common/network';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { NgxBottomSheetComponent, NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { PinLayoutComponent, PinLayoutService } from '@client-monorepo/common/pin';

@Component({
  selector: 'dpx-main',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NgxBottomNavigationWrapperComponent,
    OverlayManagerComponent,
    ErrorPageComponent,
    NgxBottomSheetComponent,
    PinLayoutComponent,
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
})
export class MainComponent implements OnInit, DoCheck, AfterViewInit, AfterViewChecked, OnDestroy {
  private storageService = inject(StorageService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private inAppMessageService = inject(InAppMessagesApiService);
  private bottomSheetService = inject(NgxBottomSheetService);
  private emitterService = inject(EmitterService);
  private backHandler = inject(BackHandlerService);

  @ViewChild('navigationArea', { static: false }) navigationArea!: ElementRef;
  containerBody = viewChild<ElementRef>('containerBody');
  layoutBody = viewChild<ElementRef>('layoutBody');
  mainLayout = viewChild<ElementRef>('mainLayout');
  emitted = false;
  expanded = signal<boolean>(true);
  hasScrolled = signal<boolean>(false);
  initialHeight = 0;
  bottomNavigationService = inject(NgxBottomNavigationService);
  routerSubscription!: Subscription;
  activatedRoute = inject(ActivatedRoute);
  supportMessengerService = inject(SupportMessengerService);
  userApiService = inject(UserApiService);
  layoutService = inject(LayoutService);
  deviceDetector = inject(DeviceDetector);
  pinLayoutService = inject(PinLayoutService);
  containerStyle = computed(() => {
    const padding = this.hasScrolled() ? this.bottomNavigationService.reservedHeight() : 0;
    return {
      paddingBottom: `${padding}px`,
    };
  });
  isVisiblePinLayout = computed(() => this.pinLayoutService.isVisible());

  loadedSupportMessenger = false;
  @ViewChild('scriptWrapper') scriptWrapper!: ElementRef<HTMLDivElement>;
  isVisibleGoftino = signal(false);
  abTestService = inject(AbTestService);
  scrolling = false;
  scrollTimeout: NodeJS.Timeout;

  ngOnInit() {
    this.backHandler.init();
    this.initComponent();
    this.subscribeOnViewportHeight();
    // This api is called for test
    setTimeout(() => {
      if (this.storageService.getToken()) {
        this.abTestService.getUserChannels().subscribe();
      }
    }, 8000);
  }

  initComponent(): void {
    const element = this.elementRef?.nativeElement?.ownerDocument.body;
    this.initialHeight = element.clientHeight;
  }

  ngAfterViewInit() {
    this.checkForInAppMessages();
    this.checkSupportMessengerVisibility();
    this.subscribeToVerticalScroll();
  }

  ngAfterViewChecked() {
    this.checkScroll();
  }

  checkSupportMessengerVisibility(): void {
    this.supportMessengerService.isVisible.subscribe((isVisible) => {
      if (this.supportMessengerService.showFloatButton()) {
        this.isVisibleGoftino.set(isVisible);
      }
      if (isVisible && !this.loadedSupportMessenger) {
        this.loadedSupportMessenger = true;
        this.addGoftinoChatToThePage();
      }
    });
  }

  checkForInAppMessages(): void {
    if (this.authService.isLoggedIn()) {
      this.inAppMessageService.getMessages().subscribe({
        next: (res) => {
          if (res.uid) {
            this.openInAppMessageBottomSheet(res);
          }
        },
      });
    }
  }

  openInAppMessageBottomSheet(res: InAppMessageApiResponseType): void {
    this.bottomSheetService.openBottomSheet(InAppMessageDialogComponent, {
      data: res,
    });
  }

  ngDoCheck() {
    this.cdr.detectChanges();
    this.checkScroll();
  }

  checkScroll(): void {
    const element = this.elementRef?.nativeElement?.querySelector('#main-layout-body-container');
    this.hasScrolled.set(element.scrollHeight > this.initialHeight);
  }

  changeTheme(): void {
    const body = this.elementRef.nativeElement.ownerDocument.body;
    if (body?.classList.contains('light')) {
      this.renderer.removeClass(body, 'light');
      this.renderer.addClass(body, 'dark');
    } else {
      this.renderer.removeClass(body, 'dark');
      this.renderer.addClass(body, 'light');
    }
  }

  handleScroll(event: any): void {
    if (this.layoutBody()?.nativeElement === event.currentTarget) {
      const pos = (this.layoutBody()?.nativeElement.scrollTop || document.body.scrollTop) + this.layoutBody()?.nativeElement.offsetHeight;
      const max = this.layoutBody()?.nativeElement.scrollHeight;
      if (pos >= max - 50) {
        if (!this.emitted) {
          this.emitterService.emitEvent(EmittingDataEnum.MAIN_LAYOUT_SCROLLED_TO_END);
          this.emitted = true;
          this.checkScroll();
        }
      } else {
        this.emitted = false;
      }
    }
  }

  setGoftinoUser(user: ProfileInterface): void {
    const script = document.createElement('script');
    script.src = 'assets/js/goftino.js';
    if ((window as any).Goftino && (window as any).Goftino.setUser) {
      (window as any).Goftino.setUser({
        email: user.email || '',
        name: user.name || user.surname ? (user.name || '') + ' ' + (user.surname || '') : '',
        phone: user.cellNumber || '',
        forceUpdate: true,
      });
    }
    if (this.scriptWrapper.nativeElement) {
      this.scriptWrapper.nativeElement.appendChild(script);
    }
  }

  toggleSupportMessenger(): void {
    this.supportMessengerService.toggleSupportMessenger();
  }

  private addGoftinoChatToThePage() {
    this.userApiService.getProfile().subscribe({
      next: (res) => {
        try {
          this.setGoftinoUser(res);
        } catch {
          window.addEventListener('goftino_ready', () => {
            this.setGoftinoUser(res);
          });
        }
      },
    });
  }

  private subscribeOnViewportHeight(): void {
    if (this.deviceDetector.isIphone() || this.deviceDetector.isSafari()) {
      this.layoutService.isKeyboardOpen().subscribe((res) => {
        let height = '';
        if (res) {
          height = 'calc(100dvh - ' + (window.innerHeight - this.layoutService.heightWhenOpenedKeyboard()) + 'px)';
        } else {
          height = '100dvh';
        }
        const layout = document.getElementsByClassName('main-layout-body')[0] as HTMLElement;

        if (layout) {
          this.renderer.setStyle(layout, 'height', height);
          this.renderer.setStyle(layout, 'max-height', height);
        }

        // const bottomSheetContainer = document.getElementsByClassName('all-width-container')[0] as HTMLElement;
        //
        // if (bottomSheetContainer) {
        //   this.renderer.setStyle(bottomSheetContainer, 'height', height);
        //   this.renderer.setStyle(bottomSheetContainer, 'max-height', height);
        // }
      });
    }
  }

  subscribeToVerticalScroll(): void {
    fromEvent(this.layoutBody().nativeElement, 'scroll').subscribe(() => {
      this.scrolling = true;
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.scrolling = false;
      }, 200);
    });

    fromEvent<TouchEvent>(this.layoutBody().nativeElement, 'touchend')
      .pipe(filter(() => this.scrolling))
      .subscribe((event: TouchEvent) => {
        if (event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.backHandler.destroy();
  }
}
